import * as React from "react"
import { useQuery } from "@tanstack/react-query"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatAmount } from "@/lib/format"
import { cn } from "@/lib/utils"

import { Advertencias } from "@/routes/holding/Advertencias"
import { AvisoDeAlcance } from "@/routes/holding/AvisoDeAlcance"
import { EstructuraPage } from "@/routes/holding/EstructuraPage"
import {
  fetchConsolidado,
  type Consolidado,
  type SeccionDeEstado,
} from "@/routes/holding/api"
import { useFiltrosGrupo } from "@/routes/holding/HoldingLayout"
import { t } from "@/lib/i18n"

/**
 * El consolidado del grupo.
 *
 * ORDEN DE LECTURA, y es deliberado: primero lo que el consolidado NO pudo
 * hacer, despues el cuadre, despues las cifras. Las advertencias abajo se leen
 * cuando ya creiste el numero.
 *
 * LA CIFRA EN ROJO ES UNA SOLA: el descuadre, y solo cuando lo hay. El
 * brandbook lo dice y aqui ademas es exacto — en un consolidado, lo unico que
 * invalida todo lo demas es que no cuadre.
 */
export function ConsolidadoPage() {
  const { periodo, moneda, libro } = useFiltrosGrupo()
  const q = useQuery({
    queryKey: ["consolidado", periodo, moneda, libro],
    queryFn: () => fetchConsolidado(periodo, moneda, libro),
  })

  const d = q.data
  return (
    <div className="space-y-3">
      {d ? <Advertencias advertencias={d.advertencias} /> : null}
      {d ? <AvisoDeAlcance alcance={d.alcance} /> : null}

      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !q.error && !d?.balance}
        emptyMessage={t("No hay nada consolidado en este período.")}
        emptyHint={t("Ninguna sociedad tiene asientos en el libro elegido. El consolidado sale de los asientos, no de las facturas.")}
        onRetry={() => void q.refetch()}
        columns={[24, 12, 12, 12, 12, 14]}
      >
        {d ? <Contenido d={d} /> : null}
      </DataState>

      {/* Quién es dueño de quién: el supuesto que el consolidado declara arriba se quita aquí. */}
      <EstructuraPage embebida />
    </div>
  )
}

function Contenido({ d }: { d: Consolidado }) {
  const cuadra = Number(d.descuadre) === 0
  return (
    <div className="space-y-3">
      {/* ------------------------------------------------ El cuadre, primero */}
      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-3 rounded-panel border px-4 py-3",
          cuadra ? "bg-surface" : "hatch-alert border-destructive/45",
        )}
      >
        <div>
          <div className="eyebrow">
            {cuadra ? "El consolidado cuadra" : "El consolidado no cuadra"}
          </div>
          <p className="mt-0.5 max-w-[62ch] text-xs text-muted-foreground">
            {cuadra
              ? "Activo igual a pasivo más patrimonio, después de convertir, sumar, eliminar y separar minoritarios."
              : "Un estado que no cuadra no es una aproximación: es un error. No lo firmes."}
          </p>
        </div>
        {cuadra ? null : (
          <div className="text-right">
            <div className="eyebrow">{t("Diferencia")}</div>
            <div className="cifra-atencion text-2xl">{formatAmount(Number(d.descuadre))}</div>
          </div>
        )}
      </div>

      {/* ---------------------------------------------- Las cifras del grupo */}
      <div className="grid gap-3 md:grid-cols-3">
        <Cifra
          etiqueta={t("Ajuste por conversión")}
          valor={Number(d.ajuste_por_conversion)}
          moneda={d.moneda_presentacion}
          nota={t("NIC 21. Va al patrimonio, en otro resultado integral — nunca al resultado del ejercicio.")}
        />
        <Cifra
          etiqueta={t("Participación no controladora")}
          valor={Number(d.participacion_no_controladora)}
          moneda={d.moneda_presentacion}
          nota={t("La parte del patrimonio y del resultado que no es del grupo.")}
        />
        <Cifra
          etiqueta={t("Eliminado entre sociedades")}
          valor={d.eliminaciones.reduce((s, e) => s + Math.abs(Number(e.importe)), 0)}
          moneda={d.moneda_presentacion}
          nota={`${d.intercompania.movimientos} movimiento(s) intercompañía detectados.`}
        />
      </div>

      {/* --------------------------------------------- Conversión por sociedad */}
      <WorkPanel
        title={t("Cómo entró cada sociedad")}
        meta={
          d.alcance && !d.alcance.completo
            ? `${d.conversion.length - 1} sociedad(es) al detalle · ${d.alcance.sociedades_agregadas} sumadas · en ${d.moneda_presentacion}`
            : `${d.conversion.length} sociedades · presentación en ${d.moneda_presentacion}`
        }
        footnote={t("El activo y el pasivo se convierten al tipo de CIERRE; el resultado, al PROMEDIO del período; el patrimonio, al HISTÓRICO —el cierre del ejercicio anterior—. Usar tres tasas es lo que rompe el cuadre, y esa diferencia es el ajuste por conversión. Si una sociedad no tiene cotización del ejercicio anterior, su patrimonio va al cierre y se avisa en la fila.")}
      >
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[16rem]">{t("Sociedad")}</TableHead>
              <TableHead className="w-14">{t("País")}</TableHead>
              <TableHead className="w-20">{t("Moneda")}</TableHead>
              <TableHead className="w-28 text-right">{t("Cierre")}</TableHead>
              <TableHead className="w-28 text-right">{t("Promedio")}</TableHead>
              <TableHead className="w-28 text-right">{t("Histórico")}</TableHead>
              <TableHead className="w-20 text-right">{t("Cuentas")}</TableHead>
              <TableHead className="w-32 text-right">{t("Ajuste conversión")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {d.conversion.map((c) => (
              <TableRow key={c.entity_id} className={cn(c.agregado && "text-muted-foreground")}>
                <TableCell
                  className={cn("truncate", c.agregado ? "italic" : "font-medium")}
                  title={c.nota ?? c.razon_social}
                >
                  {c.razon_social}
                </TableCell>
                <TableCell className="code">{c.pais ?? "—"}</TableCell>
                <TableCell className="code">{c.moneda_funcional ?? "—"}</TableCell>
                {c.agregado ? (
                  /*
                    La fila que suma las sociedades fuera de tu alcance. No lleva
                    tasas ni numero de cuentas —serian de varias sociedades a la
                    vez y no querrian decir nada—, pero SI lleva su ajuste por
                    conversion sumado: sin el, la columna no cuadraria con la
                    cifra de arriba y la tabla se leeria como si le faltara algo.
                  */
                  <>
                    <TableCell colSpan={4} className="text-xs text-muted-foreground">
                      {c.sin_libro_en
                        ? `${c.sociedades} sociedades, ${c.sin_libro_en} sin libro en este período`
                        : `${c.sociedades} sociedades`}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "num",
                        Number(c.ajuste_por_conversion ?? 0) === 0 ? "num-zero" : "num-strong",
                      )}
                    >
                      {formatAmount(Number(c.ajuste_por_conversion ?? 0))}
                    </TableCell>
                  </>
                ) : c.sin_libro ? (
                  <TableCell colSpan={5} className="text-xs text-destructive">
                    {t("Sin libro: no aporta nada al consolidado")}</TableCell>
                ) : (
                  <>
                    <TableCell className="num text-muted-foreground">
                      {c.cierre ? formatAmount(Number(c.cierre), { decimals: 6 }) : "—"}
                    </TableCell>
                    <TableCell className="num text-muted-foreground">
                      {c.promedio ? formatAmount(Number(c.promedio), { decimals: 6 }) : "—"}
                    </TableCell>
                    <TableCell
                      className={cn("num", c.detalle_tasa?.nota_historico ? "text-warning" : "text-muted-foreground")}
                      title={c.detalle_tasa?.nota_historico ?? (c.detalle_tasa?.historico_de ? `${t("cierre del ejercicio anterior, al")} ${c.detalle_tasa.historico_de}` : undefined)}
                    >
                      {c.historico ? formatAmount(Number(c.historico), { decimals: 6 }) : "—"}
                    </TableCell>
                    <TableCell className="num text-muted-foreground">{c.cuentas ?? 0}</TableCell>
                    <TableCell
                      className={cn(
                        "num",
                        Number(c.ajuste_por_conversion ?? 0) === 0 ? "num-zero" : "num-strong",
                      )}
                    >
                      {formatAmount(Number(c.ajuste_por_conversion ?? 0))}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </WorkPanel>

      {/* ------------------------------------------------------- Los estados */}
      <div className="grid gap-3 xl:grid-cols-2">
        <Estado
          titulo={t("Estado de resultados consolidado")}
          secciones={d.resultado?.secciones}
          moneda={d.moneda_presentacion}
          nota={`Del ${d.desde} al ${d.hasta}. Ya sin lo que las sociedades se facturan entre sí.`}
        />
        <Estado
          titulo={t("Balance general consolidado")}
          secciones={d.balance?.secciones}
          moneda={d.moneda_presentacion}
          nota={`Al ${d.hasta}. Incluye el ajuste por conversión y la participación no controladora dentro del patrimonio.`}
        />
      </div>

      {/* ------------------------------------------------- La base normativa */}
      <div className="rounded-md border bg-surface-sunken px-3 py-2">
        <div className="flex items-center gap-2">
          <Badge variant={d.base_normativa.es_niif ? "success-soft" : "warning-soft"} estado>
            {d.base_normativa.es_niif ? "NIIF" : "Norma local"}
          </Badge>
          <span className="eyebrow">{t("Base normativa del consolidado")}</span>
        </div>
        <p className="mt-1 max-w-[95ch] text-xs leading-[1.2rem] text-muted-foreground">
          {d.base_normativa.nota}
        </p>
      </div>
    </div>
  )
}

function Cifra({
  etiqueta,
  valor,
  moneda,
  nota,
}: {
  etiqueta: string
  valor: number
  moneda: string
  nota: string
}) {
  return (
    <div className="panel px-4 py-3">
      <div className="eyebrow">{etiqueta}</div>
      <div className={cn("cifra mt-1 text-xl", valor === 0 && "text-muted-foreground/50")}>
        {formatAmount(valor)}{" "}
        <span className="code text-2xs font-normal text-muted-foreground">{moneda}</span>
      </div>
      <p className="mt-1 text-2xs leading-[0.95rem] text-muted-foreground">{nota}</p>
    </div>
  )
}

function Estado({
  titulo,
  secciones,
  moneda,
  nota,
}: {
  titulo: string
  secciones?: SeccionDeEstado[]
  moneda: string
  nota: string
}) {
  return (
    <WorkPanel title={titulo} meta={moneda} footnote={nota}>
      {!secciones || secciones.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">
          {t("Sin movimiento en el período.")}</p>
      ) : (
        <Table className="table-dense">
          <TableBody>
            {secciones.map((s) => (
              /*
                La `key` va en el FRAGMENTO, que es lo que devuelve el map, no en
                la primera fila de dentro. Puesta dentro, React avisa por
                consola y pierde la identidad de la sección al reordenar.
              */
              <React.Fragment key={s.clave}>
                <TableRow className="row-group">
                  <TableCell className="font-medium">{s.etiqueta}</TableCell>
                  <TableCell className="num-strong w-40">{formatAmount(s.importe)}</TableCell>
                </TableRow>
                {s.cuentas.map((c) => (
                  <TableRow key={`${s.clave}-${c.codigo}`}>
                    <TableCell className="truncate pl-6 text-muted-foreground" title={c.nombre}>
                      <span className="code mr-2 text-2xs">{c.codigo}</span>
                      {c.nombre}
                    </TableCell>
                    <TableCell className={cn("num", c.importe === 0 && "num-zero")}>
                      {formatAmount(c.importe)}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </WorkPanel>
  )
}
